import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectCateringCartFilters, selectCateringCartPackages, selectCateringPackages } from "../../redux/features/restaurants/restaurantSelectors";
import { setCateringCartFilters } from "../../redux/features/restaurants/restaurantSlice";
import { getAvailableTimeSlotsForPackages, getTodayDate, isDateWithinCutoff } from "../../utils/timeSlots";

const TimeSlotSelector = ({ selectedPackage = null }) => {
  const dispatch = useDispatch();
  const filters = useSelector(selectCateringCartFilters);
  const cateringCartPackages = useSelector(selectCateringCartPackages);
  const cateringPackages = useSelector(selectCateringPackages);
  const today = getTodayDate();
  
  // Map catering packages to UI format for filtering
  const mapCateringPackagesToUIFormat = (apiPackages) => {
    if (!apiPackages || !Array.isArray(apiPackages)) return [];

    return apiPackages.map(pkg => ({
      id: pkg.id,
      restaurant_id: pkg.restaurant_id,
      cuttOffTimeInHours: pkg.cutt_off_time_in_hours,
      unavailabilitySchedule: pkg.unavailability_schedule || [],
    }));
  };

  // Get processed packages from Redux data for filtering
  // If selectedPackage is provided (package detail page), use only that package
  // Otherwise use all packages (main packages page)
  const packages = selectedPackage 
    ? [{
        id: selectedPackage.id,
        restaurant_id: selectedPackage.restaurant_id,
        cuttOffTimeInHours: selectedPackage.cutt_off_time_in_hours,
        unavailabilitySchedule: selectedPackage.unavailability_schedule || [],
      }]
    : mapCateringPackagesToUIFormat(cateringPackages);
  
  // Get all time slots including disabled ones with package constraints
  const allSlots = getAvailableTimeSlotsForPackages(filters.date, today, packages, true);
  
  // If there's a selected time that's not in available slots (e.g., from past order restoration),
  // add it to the available slots so it can be displayed as selected
  const selectedTime = filters.time;
  const isSelectedTimeInList = selectedTime && allSlots.some(slot => slot.value === selectedTime);
  
  let slotsToDisplay = allSlots;
  if (selectedTime && !isSelectedTimeInList) {
    // Add the selected time slot to the beginning of the list (from past order)
    const restoredSlot = {
      value: selectedTime,
      label: `${selectedTime} (from past order)`,
      startTime: selectedTime.split(' - ')[0],
      endTime: selectedTime.split(' - ')[1],
      disabled: false
    };
    slotsToDisplay = [restoredSlot, ...allSlots];
  }

  const handleTimeChange = (e) => {
    const selectedTime = e.target.value;
    
    // Validate the selected time slot against all packages
    // Skip validation if this is a restored time slot from past order
    if (selectedTime && packages.length > 0) {
      const selectedSlot = slotsToDisplay.find(slot => slot.value === selectedTime);
      const isRestoredSlot = selectedSlot && selectedSlot.label && selectedSlot.label.includes('(from past order)');
      
      // Don't allow selecting disabled slots
      if (selectedSlot && selectedSlot.disabled && !isRestoredSlot) {
        // Check if any package has cutoff time restrictions
        const cutoffPackage = packages.find(pkg => pkg.cuttOffTimeInHours);
        if (cutoffPackage) {
          const cutoffHours = parseInt(cutoffPackage.cuttOffTimeInHours.match(/(\d+)/)?.[1] || '0');
          alert(`This time slot is not available. Orders must be placed at least ${cutoffHours} hours in advance.`);
        } else {
          alert('This time slot is not available due to package restrictions.');
        }
        return;
      }
    }
    
    dispatch(setCateringCartFilters({ ...filters, time: selectedTime }));
  };

  return (
    <div className="flex items-center gap-4">
      <span className="font-bold text-lg text-gray-400">Time</span>
      <select
        className="border border-gray-300 rounded-lg px-3 py-2 text-gray-500 font-bold w-48"
        value={filters.time || ""}
        onChange={handleTimeChange}
        disabled={!filters.date || slotsToDisplay.length === 0}
      >
        <option value="">
          {!filters.date 
            ? "Select date first" 
            : slotsToDisplay.length === 0 
            ? (packages.some(pkg => pkg.cuttOffTimeInHours) && filters.date === today
                ? "No slots available (cutoff time)"
                : "No slots available")
            : "Select time"
          }
        </option>
        {slotsToDisplay.map(slot => (
          <option 
            key={slot.value} 
            value={slot.value}
            disabled={slot.disabled}
            className={slot.disabled ? "text-gray-400" : ""}
          >
            {slot.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default TimeSlotSelector;
